import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import SuperadminArchivedHeader, { useShowArchived } from "../components/SuperadminArchivedHeader";
import {
  ApiError,
  api,
  type AuthUser,
  type SuperadminDeletionPreview,
  type Tournament,
} from "../lib/api";

// El listado superadmin agrega admin_count por torneo.
type TournamentRow = Tournament & { admin_count?: number; owner_name?: string | null };
import {
  confirmNameMatches,
  deletionCountLines,
  deletionIsEmpty,
} from "../lib/superadminTournamentDeletion";
import {
  SHOW_ARCHIVED_KEYS,
  isArchivedTournament,
  isGovernanceTombstone,
  splitArchived,
  superadminStatusLabel,
} from "../lib/superadminArchived";

/**
 * Gobierno de torneos (superadmin): borrar un torneo con TODO su historial.
 *
 * Contrato (#166): se van todas las estadísticas del torneo, pero los
 * jugadores NO se eliminan — conservan su cuenta y su historia en los demás
 * torneos. El flujo es deliberadamente lento: primero el preview con conteos
 * y advertencias, después escribir el nombre exacto, y solo entonces el botón.
 */
export default function SuperadminTournamentsPage() {
  const { token } = useAuth();
  const [tournaments, setTournaments] = useState<TournamentRow[]>([]);
  const [admins, setAdmins] = useState<AuthUser[]>([]);
  // Asignación de torneo a admin (pedido URGENTE de Eduardo 2026-08-14):
  // el endpoint existía desde siempre; esta es su primera UI.
  const [assignDrafts, setAssignDrafts] = useState<Record<number, string>>({});
  // Eduardo 2026-09-15 / 2026-10-03: los archivados (incluidas las lápidas
  // 'ID N archivado por herramienta de gobierno') se ocultan por default; la
  // casilla «Mostrar archivados (N)» los muestra. Los cerrados se ven siempre.
  const [showArchived, setShowArchived] = useShowArchived(SHOW_ARCHIVED_KEYS.gobierno);
  const { visible: visibleTournaments, archivedCount, hiddenCount } = splitArchived(
    tournaments,
    showArchived,
  );
  const [assigningId, setAssigningId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  // Un solo torneo "armado" a la vez: abrir otro cierra el anterior.
  const [armedId, setArmedId] = useState<number | null>(null);
  const [preview, setPreview] = useState<SuperadminDeletionPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmName, setConfirmName] = useState("");
  const [deleting, setDeleting] = useState(false);
  // Rediseño 2026-10-09: búsqueda, chips, «Cambiar organizador» y menú «⋯».
  const [search, setSearch] = useState("");
  const [chip, setChip] = useState<"all" | "unassigned" | "active">("all");
  const [changeOpenId, setChangeOpenId] = useState<number | null>(null);
  const [menuId, setMenuId] = useState<number | null>(null);

  useEffect(() => {
    if (menuId === null) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setMenuId(null);
    }
    function onPointer(event: MouseEvent) {
      const target = event.target as HTMLElement | null;
      if (!target?.closest?.("[data-row-menu]")) setMenuId(null);
    }
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [menuId]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const [response, adminsResponse] = await Promise.all([
        api.superadmin.listAllTournaments(token),
        api.superadmin.listAdmins(token),
      ]);
      setTournaments(response.data.tournaments);
      setAdmins(
        adminsResponse.data.admins
          .filter((admin) => admin.status !== "disabled")
          .sort((a, b) => a.full_name.localeCompare(b.full_name, "es", { sensitivity: "base" })),
      );
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message || "No pudimos cargar los torneos."
          : "Error de red. Intenta otra vez.",
      );
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleArm(tournament: Tournament) {
    if (!token) return;
    setMessage(null);
    setError(null);
    setConfirmName("");
    setPreview(null);
    setArmedId(tournament.id);
    setPreviewLoading(true);
    try {
      const response = await api.superadmin.tournamentDeletionPreview(
        token,
        tournament.id,
      );
      setPreview(response.data);
    } catch (err) {
      setArmedId(null);
      setError(
        err instanceof ApiError
          ? err.message || "No pudimos consultar el preview."
          : "Error de red. Intenta otra vez.",
      );
    } finally {
      setPreviewLoading(false);
    }
  }

  async function handleAssignAdmin(row: TournamentRow) {
    if (!token) return;
    const draft = assignDrafts[row.id];
    if (!draft) return;
    setAssigningId(row.id);
    setError(null);
    setMessage(null);
    try {
      // Se asigna como DUEÑO: el organizador recibe el torneo completo
      // (incluida la gestión de su equipo cuando aterrice #177).
      await api.superadmin.assignTournamentAdmin(token, row.id, {
        user_id: Number(draft),
        role_in_tournament: "owner",
      });
      const admin = admins.find((item) => String(item.id) === draft);
      setMessage(`"${row.name}" asignado a ${admin?.email ?? "el admin"} como dueño.`);
      setAssignDrafts((prev) => ({ ...prev, [row.id]: "" }));
      setChangeOpenId(null);
      await load();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message || "No se pudo asignar."
          : "Error de red. Intenta otra vez.",
      );
    } finally {
      setAssigningId(null);
    }
  }

  function handleDisarm() {
    setArmedId(null);
    setPreview(null);
    setConfirmName("");
  }

  // Ocultar los archivados no debe dejar abierto el panel «Eliminar …» de un
  // torneo que la lista ya no muestra: se cierra (revisión 2026-10-03).
  function handleToggleArchived(value: boolean) {
    setShowArchived(value);
    if (
      armedId !== null &&
      !splitArchived(tournaments, value).visible.some((row) => row.id === armedId)
    ) {
      handleDisarm();
    }
  }

  const nameMatches = useMemo(
    () =>
      preview !== null && confirmNameMatches(preview.tournament.name, confirmName),
    [preview, confirmName],
  );

  async function handleDelete() {
    if (!token || armedId === null || preview === null || !nameMatches) return;
    setDeleting(true);
    setError(null);
    try {
      const response = await api.superadmin.deleteTournamentWithHistory(
        token,
        armedId,
        confirmName,
      );
      setMessage(
        `Torneo "${response.data.tournament_name}" eliminado con todo su historial. ` +
          "Los jugadores conservan su cuenta y sus demás torneos.",
      );
      handleDisarm();
      await load();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message || "No se pudo eliminar el torneo."
          : "Error de red. Intenta otra vez.",
      );
    } finally {
      setDeleting(false);
    }
  }

  // Rediseño 2026-10-09: búsqueda y chips sobre lo que ya se ve (los
  // archivados siguen dependiendo de «Mostrar archivados»).
  const needle = search.trim().toLowerCase();
  const unassignedCount = visibleTournaments.filter((row) => !isGovernanceTombstone(row.name) && (row.admin_count ?? 0) === 0).length;
  const activeCount = visibleTournaments.filter((row) => row.status === "active").length;
  const filteredTournaments = visibleTournaments.filter((row) => {
    if (chip === "unassigned" && (isGovernanceTombstone(row.name) || (row.admin_count ?? 0) > 0)) return false;
    if (chip === "active" && row.status !== "active") return false;
    if (!needle) return true;
    return (
      row.name.toLowerCase().includes(needle) ||
      (row.owner_name ?? "").toLowerCase().includes(needle) ||
      String(row.id) === needle.replace(/^#/, "")
    );
  });

  function renderAssign(tournament: TournamentRow) {
    return (
      <div className="gov-tournament__assign">
        <select
          className="t123-select adm-tourn__select"
          aria-label={`Asignar ${tournament.name} a un admin`}
          value={assignDrafts[tournament.id] ?? ""}
          onChange={(event) =>
            setAssignDrafts((prev) => ({ ...prev, [tournament.id]: event.target.value }))
          }
          disabled={assigningId === tournament.id}
        >
          <option value="">Asignar a…</option>
          {admins.map((admin) => (
            <option key={admin.id} value={admin.id}>
              {admin.full_name} ({admin.email})
            </option>
          ))}
        </select>
        <button
          className="btn btn--compact adm-players__edit"
          type="button"
          onClick={() => void handleAssignAdmin(tournament)}
          disabled={assigningId === tournament.id || !assignDrafts[tournament.id]}
        >
          {assigningId === tournament.id ? "Asignando…" : "Asignar"}
        </button>
      </div>
    );
  }

  return (
    <div className="adm-players sa-gov">
      <header className="adm-players__head">
        <div>
          <span className="adm-players__eyebrow">Superadmin</span>
          <h1 className="adm-players__title">Gobierno de torneos</h1>
          <p className="muted sa-section-sub">
            Asigna cada torneo a su organizador o bórralo con todo su historial. Los jugadores conservan su cuenta.
          </p>
        </div>
        <div className="adm-players__head-actions">
          <Link className="adm-players__btn adm-players__btn--secondary sa-link-btn" to="/superadmin">
            ← Volver
          </Link>
        </div>
      </header>
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      {message && <p className="form-message">{message}</p>}

      <section className="adm-players__panel">
        <SuperadminArchivedHeader
          shownCount={visibleTournaments.length}
          archivedCount={archivedCount}
          hiddenCount={hiddenCount}
          showArchived={showArchived}
          onToggle={handleToggleArchived}
          disabled={deleting || previewLoading}
        />
        <div className="adm-players__toolbar">
          <label className="adm-players__search">
            <span aria-hidden="true">⌕</span>
            <input
              type="search"
              placeholder="Buscar torneo u organizador"
              aria-label="Buscar torneo u organizador"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
          <div className="adm-players__chips" role="group" aria-label="Filtrar torneos">
            <button
              type="button"
              className={`adm-players__filter${chip === "all" ? " is-active" : ""}`}
              aria-pressed={chip === "all"}
              onClick={() => setChip("all")}
            >
              Todos <span className="adm-fin__chip-count">{visibleTournaments.length}</span>
            </button>
            <button
              type="button"
              className={`adm-players__filter sa-chip--warn${chip === "unassigned" ? " is-active" : ""}`}
              aria-pressed={chip === "unassigned"}
              onClick={() => setChip("unassigned")}
            >
              Sin organizador <span className="adm-fin__chip-count">{unassignedCount}</span>
            </button>
            <button
              type="button"
              className={`adm-players__filter${chip === "active" ? " is-active" : ""}`}
              aria-pressed={chip === "active"}
              onClick={() => setChip("active")}
            >
              Activos <span className="adm-fin__chip-count">{activeCount}</span>
            </button>
          </div>
        </div>

        {loading ? (
          <p className="muted">Cargando…</p>
        ) : tournaments.length === 0 ? (
          <p className="muted">No hay torneos en la plataforma.</p>
        ) : visibleTournaments.length === 0 ? (
          <p className="muted" data-no-toast>
            Todos los torneos están archivados. Marca «Mostrar archivados» para verlos.
          </p>
        ) : filteredTournaments.length === 0 ? (
          <p className="muted">Ningún torneo coincide con la búsqueda o el filtro.</p>
        ) : (
          <div className="gov-tournament-list sa-gov__list">
            {filteredTournaments.map((tournament) => {
              // La lápida no se asigna a nadie; todo archivado se ve atenuado.
              const tombstone = isGovernanceTombstone(tournament.name);
              const archived = isArchivedTournament(tournament);
              const adminCount = tournament.admin_count ?? 0;
              const unassigned = adminCount === 0;
              const menuOpen = menuId === tournament.id;
              return (
                <article key={tournament.id} className={`gov-tournament sa-gov__row${archived ? " gov-tournament--tombstone" : ""}`}>
                  <div className="sa-gov__name">
                    <Link className="gov-tournament__name" to={`/admin/tournaments/${tournament.id}`}>
                      {tournament.name}
                    </Link>
                    <small className="muted">
                      #{tournament.id} · {tournament.start_date ?? "Sin fechas"}
                      {tournament.end_date ? ` – ${tournament.end_date}` : ""}
                    </small>
                  </div>
                  <div className="sa-gov__owner">
                    {unassigned ? (
                      <span className="adm-tourn__chip adm-tourn__chip--warn">Sin organizador</span>
                    ) : (
                      <span>
                        {tournament.owner_name ?? "Asignado"}
                        {adminCount > 1 ? <small className="muted"> +{adminCount - 1}</small> : null}
                      </span>
                    )}
                  </div>
                  <div>
                    <span className={`adm-tourn__chip${tournament.status === "active" ? " adm-tourn__chip--live" : tournament.status === "draft" ? " adm-tourn__chip--draft" : ""}`}>
                      {superadminStatusLabel(tournament.status)}
                    </span>
                  </div>
                  <div className="gov-tournament__actions sa-gov__actions">
                    {tombstone ? null : unassigned || changeOpenId === tournament.id ? (
                      renderAssign(tournament)
                    ) : (
                      <button
                        type="button"
                        className="btn btn--ghost btn--compact"
                        onClick={() => setChangeOpenId(tournament.id)}
                      >
                        Cambiar organizador
                      </button>
                    )}
                    {armedId === tournament.id ? (
                      <button className="btn btn--ghost btn--compact" type="button" onClick={handleDisarm} disabled={deleting}>
                        Cancelar
                      </button>
                    ) : (
                      <div className="adm-players__menu" data-row-menu>
                        <button
                          type="button"
                          className="btn btn--ghost btn--compact adm-players__menu-trigger"
                          aria-haspopup="menu"
                          aria-expanded={menuOpen}
                          aria-label={`Más acciones para ${tournament.name}`}
                          onClick={() => setMenuId(menuOpen ? null : tournament.id)}
                        >
                          ⋯
                        </button>
                        {menuOpen ? (
                          <div className="adm-players__menu-list" role="menu">
                            <button
                              type="button"
                              role="menuitem"
                              className="adm-players__menu-item adm-players__menu-item--danger"
                              onClick={() => {
                                setMenuId(null);
                                void handleArm(tournament);
                              }}
                              disabled={deleting || previewLoading}
                            >
                              Eliminar…
                            </button>
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      {armedId !== null && (
        <section className="sa-delete" aria-live="polite">
          {previewLoading ? (
            <p className="muted">Consultando qué se destruiría…</p>
          ) : preview ? (
            <>
              <h2 className="sa-delete__title">¿Borrar «{preview.tournament.name}»?</h2>
              <p className="sa-delete__sub">Esta acción es irreversible y no tiene vuelta atrás.</p>

              {deletionIsEmpty(preview.counts) ? (
                <p>Este torneo no tiene historial: se elimina solo su configuración.</p>
              ) : (
                <>
                  <p>Se eliminará de forma permanente:</p>
                  <ul className="sa-delete__counts">
                    {deletionCountLines(preview.counts).map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                </>
              )}

              {preview.warnings.map((warning) => (
                <p key={warning} className="form-error" role="alert">
                  ⚠ {warning}
                </p>
              ))}

              <p className="sa-delete__keep">{preview.preserved}</p>

              <div className="form">
                <label>
                  Escribe el nombre exacto del torneo para confirmar
                  <input
                    type="text"
                    value={confirmName}
                    placeholder={preview.tournament.name}
                    onChange={(event) => setConfirmName(event.target.value)}
                    disabled={deleting}
                    autoComplete="off"
                  />
                  {confirmName.trim().length > 0 && !nameMatches && (
                    <span className="form__hint">El nombre no coincide todavía.</span>
                  )}
                </label>
                <div className="form__actions">
                  <button className="btn btn--ghost" type="button" onClick={handleDisarm} disabled={deleting}>
                    Cancelar
                  </button>
                  <button
                    className="btn btn--danger"
                    type="button"
                    onClick={() => void handleDelete()}
                    disabled={!nameMatches || deleting}
                  >
                    {deleting ? "Eliminando…" : "Borrar torneo y su historial"}
                  </button>
                </div>
              </div>
            </>
          ) : null}
        </section>
      )}
    </div>
  );
}
